import React from "react";
import { Link } from "gatsby";
import PropTypes from "prop-types";

/**
 * @typedef {Object} LinkData
 * @property {string} id - required id for the link element.
 * @property {string} text - required text to show for the link.
 * @property {string} url - required string url for the link to go.
 * @property {boolean} show - boolean value to show or hide the link.
 * @property {boolean} externalLink - boolean value to use an `<a>` or `<Link>` element.
 */

/**
 * This component renders the footer links on the footer in columns.
 *
 * @param {Object} props - The object containing props.
 * @param {string} props.id - id of the main category link at the top of the column.
 * @param {string} props.title - title of the main category link at the top of the column.
 * @param {string} props.url - required url of the main category link at the top of the column.
 * @param {LinkData[]} props.links - required array of objects to display and fill the column.
 * @returns {React.JSX.Element} Returns the JSX component to render.
 */
const FooterList = ({ id = "footer-list", title = "Footer List", url = "", links = [] }) => (
  <ul className="nav flex-column mr-3 mb-3 mb-lg-0">
    <li className="nav-item mb-2 pb-2 text-sm">
      <strong>
        <Link id={id} to={url}>
          {title}
        </Link>
      </strong>
    </li>
    {links.map((item) =>
      item.show ? (
        <li key={item.id} className={"nav-item pb-2 text-sm"}>
          {item.externalLink ? (
            <a href={item.url} id={item.id} target="_blank" rel="noopener noreferrer" className="text-secondary">
              {item.text}
            </a>
          ) : (
            <Link id={item.id} to={item.url} className="text-secondary">
              {item.text}
            </Link>
          )}
        </li>
      ) : null
    )}
  </ul>
);

export default FooterList;

FooterList.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      show: PropTypes.bool,
      externalLink: PropTypes.bool
    })
  ).isRequired
};
